import React, { useState, useEffect } from "react";
import { useForm, SubmitHandler } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { PaymentMethodModel } from "@/apidtos";
import { LabeledInput } from "@core/components/forms/LabeledInput";
import { Button } from "@core/components/ui/button";
import { Surface } from "@core/components/ui/surface";
import { PaymentMethodSelector } from "@phoenix/payments/components/PaymentMethodSelector";
import { useApi } from "@core/api";
import { useCurrencies } from "@/plugins/products/hooks/useCurrencies";
import { useRootLoaderData } from "@/root";
import {
  collectSurchargeSalesTransactionIds,
  InterpaymentsCancelReasonCode,
} from "@phoenix/payments/lib/interpaymentsSurchargeCancel";

interface Props {
  methods: PaymentMethodModel[];
  totalBalance: number;
  onSubmit: (amount: number, walletId: number, expectedTotal: number) => Promise<void>;
  /**
   * Parent dialog open state; when it goes from true to false, pending Interpayments surcharge quotes are cancelled.
   */
  dialogOpen?: boolean;
}

interface FormFields {
  amount: number;
}

export const PayOnAccount: React.FC<Props> = ({ methods, totalBalance, onSubmit, dialogOpen: dialogOpenFromParent }) => {
  const { t } = useTranslation();

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<FormFields>({
    defaultValues: { amount: totalBalance },
    mode: "onChange",
  });
  const api = useApi();
  const watchedAmount = watch("amount");
  const [selectedPaymentMethodId, setSelectedPaymentMethodId] = useState<number | null>(null);
  const [surchargeAmount, setSurchargeAmount] = useState<number | null>(null);
  const [payPrincipalOnlyAfterFeeFailure, setPayPrincipalOnlyAfterFeeFailure] = useState(false);
  const pendingSurchargeSalesTxIdsRef = React.useRef<string[]>([]);
  const prevDialogOpenRef = React.useRef<boolean | undefined>(undefined);
  const surchargeCancelPrimedRef = React.useRef(false);
  const prevPmForSurchargeRef = React.useRef<number | null>(null);
  const prevAmtForSurchargeRef = React.useRef(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const currency = useCurrencies();

  const { config } = useRootLoaderData();
  // @ts-expect-error Can't strongly type without depending on surcharging plugin
  const surchargeEnabled = config["Surcharging"]?.["SurchargeEnabled"] as boolean;

  const postCancelCalculatedSurcharge = React.useCallback(
    async (reasonCode: string) => {
      const ids = [...pendingSurchargeSalesTxIdsRef.current];
      if (ids.length === 0) {
        return;
      }
      try {
        await api["/invoicing/cancelCalculatedSurcharge"]({
          SalesTransactionIds: ids,
          ReasonCode: reasonCode,
        });
      } catch (e) {
        console.error("cancelCalculatedSurcharge failed:", e);
      } finally {
        pendingSurchargeSalesTxIdsRef.current = [];
      }
    },
    [api],
  );

  React.useEffect(() => {
    if (dialogOpenFromParent === undefined) {
      return;
    }
    if (prevDialogOpenRef.current === true && dialogOpenFromParent === false) {
      void postCancelCalculatedSurcharge(InterpaymentsCancelReasonCode.Abandoned);
    }
    prevDialogOpenRef.current = dialogOpenFromParent;
  }, [dialogOpenFromParent, postCancelCalculatedSurcharge]);

  useEffect(() => {
    if (!surchargeCancelPrimedRef.current) {
      surchargeCancelPrimedRef.current = true;
      prevPmForSurchargeRef.current = selectedPaymentMethodId;
      prevAmtForSurchargeRef.current = watchedAmount;
      return;
    }

    let reason: string | null = null;
    const ids = pendingSurchargeSalesTxIdsRef.current;

    if (ids.length > 0) {
      const pmPrev = prevPmForSurchargeRef.current;
      const amtPrev = prevAmtForSurchargeRef.current;
      const pmChanged = pmPrev !== selectedPaymentMethodId;
      const amountChanged = amtPrev !== watchedAmount;

      if (pmChanged && selectedPaymentMethodId != null) {
        const prevM = methods.find((m: PaymentMethodModel) => m.Id === pmPrev);
        const currM = methods.find((m: PaymentMethodModel) => m.Id === selectedPaymentMethodId);
        if (currM?.TypeId === "ACH") {
          reason = InterpaymentsCancelReasonCode.SwitchedPaymentToAch;
        } else if (
          prevM?.TypeId === "Credit" &&
          currM?.TypeId === "Credit" &&
          pmPrev !== selectedPaymentMethodId
        ) {
          reason = InterpaymentsCancelReasonCode.Other;
        } else if (pmChanged) {
          reason = InterpaymentsCancelReasonCode.Other;
        }
      } else if (amountChanged && !pmChanged) {
        reason = InterpaymentsCancelReasonCode.Other;
      }

      if (reason) {
        void postCancelCalculatedSurcharge(reason);
      }
    }

    prevPmForSurchargeRef.current = selectedPaymentMethodId;
    prevAmtForSurchargeRef.current = watchedAmount;

    setSurchargeAmount(null);
    setPayPrincipalOnlyAfterFeeFailure(false);
  }, [
    watchedAmount,
    selectedPaymentMethodId,
    methods,
    postCancelCalculatedSurcharge,
  ]);

  useEffect(
    () => () => {
      void postCancelCalculatedSurcharge(InterpaymentsCancelReasonCode.Abandoned);
    },
    [postCancelCalculatedSurcharge],
  );

  useEffect(() => {
    setValue("amount", parseFloat(totalBalance.toFixed(2)));
  }, [setValue, totalBalance]);

  const canCalcSurcharge = watchedAmount > 0 && !!selectedPaymentMethodId;
  const selectedPaymentMethod = methods.find(
    (m: PaymentMethodModel) => m.Id === selectedPaymentMethodId
  );
  const isACH = selectedPaymentMethod?.TypeId === "ACH";
  const isCredit = selectedPaymentMethod?.TypeId === "Credit";
  const maxAmount = isCredit ? (Math.abs(selectedPaymentMethod?.CurrentBalance ?? 0)) : totalBalance;
  const needsSurchargeBeforePay =
    surchargeEnabled && !!selectedPaymentMethod && !isACH && !isCredit;
  const canSubmit =
    watchedAmount > 0 &&
    !!selectedPaymentMethodId &&
    (!needsSurchargeBeforePay ||
      typeof surchargeAmount === "number" ||
      payPrincipalOnlyAfterFeeFailure);
  
  useEffect(() => {
    if (selectedPaymentMethod?.TypeId === "Credit") {
      const creditBalance = selectedPaymentMethod.CurrentBalance ?? 0;
      setValue("amount", parseFloat(Math.abs(creditBalance).toFixed(2)));
    }
  }, [selectedPaymentMethod, setValue]);

  const handleSurchargeCalculation = async () => {
    if (!canCalcSurcharge || !selectedPaymentMethodId) return;
    try {
      const result = await api.PayOnAccountSurcharge({
        WalletId: selectedPaymentMethodId,
        PaymentTotal: watchedAmount,
      });
      if (result.IsSuccess && result.Result) {
        setSurchargeAmount(result.Result.TotalSurcharge ?? 0);
        pendingSurchargeSalesTxIdsRef.current = collectSurchargeSalesTransactionIds(
          result.Result.SurchargeDetails,
        );
        setPayPrincipalOnlyAfterFeeFailure(false);
      } else {
        console.error("Surcharge API errors:", result.Messages);
        setPayPrincipalOnlyAfterFeeFailure(true);
      }
    } catch (err) {
      console.error("Network or unexpected error:", err);
      setPayPrincipalOnlyAfterFeeFailure(true);
    }
  };

  const onFormSubmit: SubmitHandler<FormFields> = async ({ amount }) => {
    if (!selectedPaymentMethodId) return;
    setIsSubmitting(true);
    try {
      await onSubmit(
        amount,
        selectedPaymentMethodId,
        surchargeEnabled &&
          typeof surchargeAmount === "number" &&
          !payPrincipalOnlyAfterFeeFailure
          ? amount + surchargeAmount
          : amount
      );
      pendingSurchargeSalesTxIdsRef.current = [];
    } catch (err) {
      console.error("Error submitting payment:", err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const onPaymentMethodChange = (id?: number) => {
    if (!id) return;
    setSelectedPaymentMethodId(id);
  };

  return (
    <form className="space-y-4" onSubmit={handleSubmit(onFormSubmit)}>
      <div>
        <p className="font-semibold">
          {t("ui.totalBalance", "Total Balance Due")}: {currency.format(totalBalance)}
        </p>
      </div>
      <LabeledInput
        type="number"
        step="0.01"
        min={0}
        defaultValue={totalBalance.toFixed(2)}
        label={t("ui.amountToPay", "Amount to Pay")}
        max={maxAmount.toFixed(2)}
        placeholder="Enter amount"
        {...register("amount", {
          required: true,
          valueAsNumber: true,
          setValueAs: (value: string) => {
            const parsed = parseFloat(value);
            if (isNaN(parsed)) return 0;
            const rounded = parseFloat(parsed.toFixed(2));
            return rounded > maxAmount ? maxAmount : rounded;
          },
          validate: (value) => {
            if (value <= 0) return "Amount must be greater than 0";
            if (value > maxAmount) return `Amount cannot exceed ${maxAmount.toFixed(2)}`;
            return true;
          }
        })}
        onBlur={(e) => {
          const enteredValue = parseFloat(e.target.value);
          if (!isNaN(enteredValue)) {
            const formattedValue = parseFloat(enteredValue.toFixed(2));
            setValue("amount", formattedValue);
          }
        }}
        errors={errors}
      />
      <Surface variant="outline">
        <PaymentMethodSelector
          onChange={onPaymentMethodChange} methods={methods} />
      </Surface>
      {surchargeEnabled && !isCredit && !isACH && (
        <div className="bg-surface-50 rounded p-4 space-y-2">
          <div className="flex flex-col text-left">
            <div className="text-base">{t("ui.balanceDue", "Balance Due")}</div>
            <div className="text-3xl"> {currency.format(watchedAmount)}</div>
          </div>
          {typeof surchargeAmount === "number" ? (
            <div className="flex gap-8">
              <div className="flex flex-col text-left">
                <div className="text-base">{t("ui.transactionFee", "Transaction Fee")}</div>
                <div className="text-3xl">{currency.format(surchargeAmount)}</div>
              </div>
              <div className="flex flex-col text-left">
                <div className="text-base">{t("ui.totalPaymentAmount", "Total Payment Amount")}</div>
                <div className="text-3xl">{currency.format(watchedAmount + surchargeAmount)}</div>
              </div>
            </div>
          ) : (
            <>
              <Button
                type="button"
                className="w-full"
                onClick={handleSurchargeCalculation}
                disabled={!canCalcSurcharge}>
                {t("ui.calculateSurcharge", "Calculate Surcharge")}
              </Button>
              {payPrincipalOnlyAfterFeeFailure && (
                <p className="text-sm text-neutral-600 dark:text-neutral-400">
                  {t(
                    "ui.cardFeeUnavailablePayPrincipal",
                    "Card fee could not be calculated. You can pay the invoice amount without a surcharge.",
                  )}
                </p>
              )}
            </>
          )}
        </div>
      )}
      <div className="flex justify-end">
        <Button type="submit" disabled={!canSubmit || isSubmitting}>
          {isSubmitting ? t("ui.submitting", "Submitting...") : t("ui.pay", "Pay")}
        </Button>
      </div>
    </form>
  );
};
